import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { programsApi, organizationsApi } from 'api';
import { useProgramPanel } from 'contexts/programPanelContext';
import { ProgramLandingPageMode, UpdateProgramBaseConfigParametersPayload } from 'types/Program';
import { Link } from 'components/_new/Link';
import { Icon } from 'components/_new/Icon';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import dayjs from 'dayjs';
import { Panel, PanelItem } from '../../partials/panel';

type ProgramStatusForm = {
	displayName: string;
	shortName: string;
	organizationDisplayName: string;
	subtitle: string;
	logotype?: File;
	finished: boolean;
	endDate?: Date;
	nextProgramName?: string;
};

export const ProgramBaseConfig = () => {
	const { t } = useTranslation();
	const { data: programData, refetch: programRefetch } = useProgramPanel();

	const editModeState = useState(false);
	const form = useForm<ProgramStatusForm>({
		defaultValues: {
			displayName: programData?.displayName,
			organizationDisplayName: programData?.organization?.displayName || '',
			shortName: programData?.shortName || '',
			subtitle: programData?.subtitle || '',
			logotype: undefined,
			finished: programData?.finished || false,
			endDate: programData?.endDate || undefined,
			nextProgramName: programData?.nextProgramName || undefined,
		},
	});
	const finisehd = form.watch('finished');

	const updateProgram = useMutation(
		(data: UpdateProgramBaseConfigParametersPayload) =>
			programsApi.putProgramAdminBaseConfigParameters(Number(programData?.id), data),
		{
			onSuccess: () => {
				programRefetch?.();
			},
		}
	);

	const updateOrganization = useMutation(
		({
			organizationName,
			displayName,
			language,
		}: {
			organizationName: string;
			displayName: string;
			language: string;
		}) => organizationsApi.editOrganization(organizationName, { displayName, language }),
		{
			onSuccess: () => {
				programRefetch?.();
			},
		}
	);

	const handleSave = async ({
		displayName,
		organizationDisplayName,
		subtitle,
		shortName,
		logotype,
		finished,
		endDate: inputEndDate,
		nextProgramName,
	}: ProgramStatusForm) => {
		const endDate = inputEndDate ? dayjs(inputEndDate).format('YYYY-MM-DD') : null;

		try {
			await updateProgram.mutateAsync({
				displayName,
				subtitle,
				shortName,
				logotype,
				finished,
				endDate,
				nextProgramName: nextProgramName || null,
			});

			if (
				programData?.isProgramOrganizationAdmin &&
				organizationDisplayName !== programData?.organization?.displayName &&
				programData?.organization?.name
			) {
				await updateOrganization.mutateAsync({
					organizationName: programData.organization.name,
					displayName: organizationDisplayName,
					language: programData.organization.language,
				});
			}

			editModeState[1](false);
		} catch (error) {
			console.error('Error updating program or organization:', error);
		}
	};

	const { data: nextPrograms } = useQuery(
		['PROGRAM_NEXT_PROGRAMS', { programId: Number(programData?.id) }],
		() => programsApi.getNextPrograms(Number(programData?.id)),
		{
			enabled: true, // finisehd || programData?.finished,
			// initialData: [],
			select: (data) =>
				data.map(({ displayName, name }) => ({
					label: displayName,
					value: name,
				})),
			onSuccess: () => {
				programRefetch?.();
			},
		}
	);

	// const programUrl = `${process.env.REACT_APP_FRONT_URL}/${programData?.name}`;
	const isOrganizationAdmin = programData?.isProgramOrganizationAdmin;

	return (
		<Panel
			title={t('programPanel.dashboard.status.titleInConfig')}
			form={form}
			editModeState={editModeState}
			isLoading={updateProgram.isLoading || updateOrganization.isLoading}
			onSave={handleSave}
		>
			<>
				<PanelItem
					title={t('programPanel.dashboard.status.programName')}
					value={programData?.displayName}
					type="string"
					controller={{ control: form.control, name: 'displayName' }}
					inputTextProps={{ maxLength: 60 }}
				/>
				<PanelItem
					title={t('programPanel.dashboard.status.programSubtitle')}
					value={programData?.subtitle}
					type="string"
					controller={{ control: form.control, name: 'subtitle' }}
				/>
				<PanelItem
					title={t('programPanel.dashboard.status.programShortName')}
					value={programData?.shortName}
					type="string"
					controller={{ control: form.control, name: 'shortName' }}
					withEmptyText
				/>
				<PanelItem
					title={t('programPanel.dashboard.status.organizationName')}
					value={programData?.organization?.displayName}
					type="string"
					controller={
						isOrganizationAdmin ? { control: form.control, name: 'organizationDisplayName' } : undefined
					}
					editable={isOrganizationAdmin}
				/>
				<PanelItem
					title={t('programPanel.dashboard.status.programLogotype')}
					value={programData?.logotype}
					type="image"
					controller={{ control: form.control, name: 'logotype' }}
				/>
				<PanelItem
					title={t('programPanel.dashboard.status.programIsActive')}
					type="boolean"
					value={!programData?.finished}
					controller={{ control: form.control, name: 'finished' }}
					transformValue="active/unactive"
					inputCheckboxProps={{ invertValue: true }}
				/>
				{(finisehd || programData?.finished) && (
					<PanelItem
						title={t('programPanel.dashboard.status.nextProgramRedirect')}
						value={programData?.nextProgramName}
						type="select"
						controller={{ control: form.control, name: 'nextProgramName' }}
						inputDropdownProps={{
							options: nextPrograms,
							noOptionsText: t('programPanel.dashboard.status.nextProgramRedirect'),
						}}
						withEmptyText="not-selected"
						bold
					/>
				)}
				<PanelItem
					title={t('programPanel.dashboard.status.endDate')}
					value={programData?.endDate}
					type="date"
					controller={{ control: form.control, name: 'endDate' }}
					clearable
					transformValue="date-only"
					withEmptyText
					bold
				/>
			</>
		</Panel>
	);
};
